<template>
  <div
    class="bg-white dark:bg-dark-surface rounded-3xl border border-slate-100 dark:border-white/5 shadow-xl overflow-hidden transition-colors"
  >
    <!-- Header -->
    <div
      class="p-6 border-b dark:border-white/5 flex justify-between items-center bg-slate-50/50 dark:bg-white/5"
    >
      <h3 class="text-sm font-black uppercase tracking-widest text-primary">
        {{ $t("recent_activity") }}
      </h3>
      <BaseButton
        type="secondary"
        size="sm"
        class="!rounded-xl !py-1 text-[10px] uppercase font-black tracking-widest"
        @click="$router.push(isAdmin ? '/admin/bookings' : '/host/bookings')"
      >
        {{ $t("view_all") }}
      </BaseButton>
    </div>

    <!-- Error State -->
    <div v-if="error" class="p-6 text-center text-red-500">
      <p class="text-xs font-bold uppercase tracking-widest">{{ error }}</p>
    </div>

    <!-- Table -->
    <div v-else class="overflow-x-auto">
      <table class="w-full">
        <thead>
          <tr class="text-left border-b dark:border-white/5">
            <th
              class="px-6 py-4 text-[10px] font-black text-slate-400 uppercase tracking-widest"
            >
              {{ $t("customer") }}
            </th>
            <th
              class="px-6 py-4 text-[10px] font-black text-slate-400 uppercase tracking-widest"
            >
              {{ $t("car") }}
            </th>
            <th
              class="px-6 py-4 text-[10px] font-black text-slate-400 uppercase tracking-widest"
            >
              {{ $t("status") }}
            </th>
            <th
              class="px-6 py-4 text-[10px] font-black text-slate-400 uppercase tracking-widest"
            >
              {{ $t("amount") }}
            </th>
          </tr>
        </thead>

        <!-- Skeleton Loader State -->
        <tbody
          v-if="loading"
          class="divide-y dark:divide-white/5 animate-pulse"
        >
          <tr v-for="i in 5" :key="`table-skeleton-${i}`">
            <td class="px-6 py-4">
              <div class="flex items-center gap-3">
                <div
                  class="w-8 h-8 rounded-lg bg-slate-200 dark:bg-dark-input/50"
                ></div>
                <div
                  class="h-2.5 bg-slate-200 dark:bg-dark-input rounded w-20"
                ></div>
              </div>
            </td>
            <td class="px-6 py-4">
              <div
                class="h-2.5 bg-slate-200 dark:bg-dark-input rounded w-28"
              ></div>
            </td>
            <td class="px-6 py-4">
              <div
                class="h-4 bg-slate-200 dark:bg-dark-input rounded-full w-16"
              ></div>
            </td>
            <td class="px-6 py-4">
              <div
                class="h-2.5 bg-slate-200 dark:bg-dark-input rounded w-10"
              ></div>
            </td>
          </tr>
        </tbody>

        <!-- Actual Data State -->
        <tbody
          v-else-if="bookings && bookings.length > 0"
          class="divide-y dark:divide-white/5"
        >
          <tr
            v-for="b in bookings"
            :key="b.id"
            class="hover:bg-slate-50 dark:hover:bg-white/5 transition-colors group"
          >
            <td class="px-6 py-4">
              <div class="flex items-center gap-3">
                <div
                  class="w-8 h-8 rounded-lg bg-slate-100 dark:bg-dark-input flex items-center justify-center text-xs font-black"
                >
                  {{ getUserInitials(b.user) }}
                </div>
                <span class="text-xs font-bold">{{
                  b.user?.username || b.user?.name || $t("user")
                }}</span>
              </div>
            </td>
            <td
              class="px-6 py-4 text-xs font-bold text-slate-500 dark:text-slate-400"
            >
              <router-link
                v-if="b.car"
                :to="{ name: 'CarView', params: { id: b.car.id } }"
                class="hover:text-primary transition-colors cursor-pointer flex items-center gap-1"
              >
                <i
                  class="fas fa-external-link-alt text-[8px] opacity-0 group-hover:opacity-100 transition-opacity"
                ></i>
                {{ b.car?.model_name || $t("car") }}
              </router-link>
              <span v-else>{{ $t("car") }}</span>
            </td>
            <td class="px-6 py-4">
              <span
                :class="`px-2 py-0.5 rounded-full text-[8px] font-black uppercase tracking-widest ${getStatusClass(
                  b.status
                )}`"
              >
                {{ $t(getStatusTextKey(b.status)) }}
              </span>
            </td>
            <td class="px-6 py-4 text-xs font-black text-primary">
              {{ formattedAmount(b.total_price) }}
            </td>
          </tr>
        </tbody>

        <!-- Empty State -->
        <tbody v-else>
          <tr>
            <td colspan="4" class="p-12 text-center text-slate-400">
              <p class="text-xs font-bold uppercase tracking-widest">
                {{ $t("no_data") }}
              </p>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>

<script>
import BaseButton from "@/components/Button.vue";
import { formatCurrency, formatBookingStatus } from "@/utils/formatters";

export default {
  name: "DashboardRecentActivity",
  components: { BaseButton },
  props: {
    bookings: {
      type: Array,
      default: () => [],
    },
    loading: {
      type: Boolean,
      default: false,
    },
    error: {
      type: String,
      default: null,
    },
    isAdmin: {
      type: Boolean,
      default: false,
    },
  },
  methods: {
    getUserInitials(user) {
      if (!user) return "U";
      const name = user.username || user.name || "";
      return name.substring(0, 2).toUpperCase() || "U";
    },
    getStatusClass(status) {
      return formatBookingStatus(status).class;
    },
    getStatusTextKey(status) {
      return formatBookingStatus(status).textKey;
    },
    formattedAmount(amount) {
      return formatCurrency(amount, this.$i18n.locale);
    },
  },
};
</script>
